import { waitFor } from 'xstate';

import { devEnvConfig } from 'config/public';
import { CONSENT_COOKIE_NAME } from 'constants/cookies';
import { ResponseError, ValidationError } from 'errors';
import type { ValidationErrorsResponse } from 'models/api';
import type { JSONValue } from 'types';

import { mapKeys } from './collection';
import { getConsentHeader } from './consent';
import { getCookie } from './cookies';
import { is, isClient, sendGlobalEvent } from './helpers';
import { log } from './log';
import { failure, type Result, success } from './result';

const JSON_MIME_TYPE = 'application/json';

export function getCloudflareAccessHeaders(): Record<string, string> {
	const id = devEnvConfig.CF_ACCESS_CLIENT_ID;
	const secret = devEnvConfig.CF_ACCESS_CLIENT_SECRET;
	return id && secret
		? { 'CF-Access-Client-Id': id, 'CF-Access-Client-Secret': secret }
		: {};
}

/** Ensure the user has a valid token. */
export async function ensureFreshToken() {
	if (isClient() && globalThis.globalFetchLockActor) {
		const tokenExpiry = globalThis.localStorage.getItem('tokenExpiry');
		if (!tokenExpiry || Date.now() > Number.parseInt(tokenExpiry, 10)) {
			sendGlobalEvent('refresh-token');
		}
		await waitFor(
			globalThis.globalFetchLockActor,
			(state) => state.matches('unlocked'),
			{ timeout: 20_000 },
		);
	}
}

function isHeadersInstance(headers: HeadersInit): headers is Headers {
	// Can't use instanceof due to tests, the global Headers here isn't
	// the same as the one used for mocking.
	// oxlint-disable-next-line typescript/unbound-method
	return 'get' in headers && is.func(headers.get);
}

/**
 * Get the value for the specified header.
 *
 * @example
 *
 * getHeaderValue(headers, 'Content-Type');
 * // => 'application/json'
 */
export function getHeaderValue(
	headers: HeadersInit,
	key: string,
): string | undefined {
	return isHeadersInstance(headers)
		? (headers.get(key) ?? undefined)
		: is.array(headers)
			? headers.find(([k]) => k.toLowerCase() === key.toLowerCase())?.[1]
			: (headers[key] ?? headers[key.toLowerCase()]);
}

/** Check if a request or response has a JSON content type. */
function isJson(headers: HeadersInit): boolean {
	const contentType = getHeaderValue(headers, 'Content-Type');
	// Use includes since Content-Type can contain things like charset as well.
	return is.string(contentType) && contentType.includes(JSON_MIME_TYPE);
}

/** Lowercase object keys. */
function lowerKeys(obj: Record<string, string>) {
	return mapKeys(obj, (val, key) => key.toLowerCase());
}

export type FetchError = ResponseError | ValidationError | Error;

export interface RequestOptions extends Omit<RequestInit, 'body'> {
	/** The request body. JSON-valid values will be stringified. */
	body?: BodyInit | JSONValue;
	/** How many times to attempt a request in case it fails. */
	maxAttempts?: number;
}

async function fetcher<T = unknown>(
	input: string | URL | Request,
	options?: RequestOptions,
	attempt = 1,
): Promise<T | undefined> {
	const isReqInput = is.instance(input, Request);
	const maxAttempts = options?.maxAttempts ?? 3;

	// The Headers object is case insensitive, probably to match the HTTP spec
	// https://www.rfc-editor.org/rfc/rfc7230#section-3.2, but it's sort of case
	// sensitive on creation in that the same header with different case will be
	// merged instead of overwritten:
	// new Headers({ 'Content-Type': 'text/plain', 'content-type': 'text/html' });
	// => Headers { "content-type": "text/plain, text/html" }
	// Making both input and defaults lowercase before merging will avoid this.
	const inputHeaders = lowerKeys({
		...(isReqInput ? Object.fromEntries(input.headers.entries()) : null),
		...(is.instance(options?.headers, Headers)
			? Object.fromEntries(options.headers.entries())
			: is.array(options?.headers)
				? Object.fromEntries(options.headers)
				: options?.headers),
	});
	const defaultHeaders = lowerKeys({
		'Pragma': inputHeaders.pragma || 'no-cache',
		'Cache-Control': inputHeaders['cache-control'] || 'no-cache',
		'Content-Type': inputHeaders['content-type'] || JSON_MIME_TYPE,
		...getCloudflareAccessHeaders(),
		...getConsentHeader(getCookie(CONSENT_COOKIE_NAME)),
	});
	const headers = new Headers({
		...inputHeaders,
		...defaultHeaders,
	});

	const bodyInput = options?.body ?? (isReqInput ? input.body : null);
	// When sending form data, the browser itself has to set the Content-Type
	// header for a proper boundary to be included.
	// https://stackoverflow.com/q/39280438
	if (bodyInput instanceof FormData) {
		headers.delete('content-type');
	}

	let body: BodyInit | undefined;
	if (is.defined(bodyInput)) {
		// Exclude string to not double stringify.
		const isJsonValue =
			is.plainObject(bodyInput) ||
			is.array(bodyInput) ||
			is.bool(bodyInput) ||
			is.number(bodyInput);
		body = isJsonValue ? JSON.stringify(bodyInput) : bodyInput;
	}

	await ensureFreshToken();

	const response = await fetch(input, {
		...options,
		cache: options?.cache ?? (isReqInput ? input.cache : null) ?? 'no-store',
		credentials:
			options?.credentials ??
			(isReqInput ? input.credentials : null) ??
			undefined,
		headers,
		body,
	});

	// response.json() will throw a parse error for empty responses so always
	// read as text and manually parse to JSON to handle it.
	const responseText = await response.text();
	const responseData: JSONValue | undefined = responseText
		? isJson(response.headers)
			? JSON.parse(responseText)
			: responseText
		: undefined;

	if (response.ok) {
		// HTTP 204 is No Content. Also resolve any empty values as undefined.
		if (response.status === 204 || is.nonZeroFalsy(responseData)) {
			return undefined;
		}
		// Handle exactly zero string to not match leading zero check below.
		if (responseData === '0') {
			return 0 as T;
		}
		// Exclude numbers with leading zero since casting it will result in
		// something different (e.g. '01' !== 1).
		if (is.numeric(responseData) && !String(responseData).startsWith('0')) {
			return Number(responseData) as T;
		}
		return responseData as T;
	}

	if (
		response.status === 400 &&
		responseData &&
		is.object(responseData) &&
		'errors' in responseData &&
		is.array(responseData.errors)
	) {
		throw new ValidationError(responseData as ValidationErrorsResponse);
	}

	// For 401 Unauthorized responses, try to refresh the user's access token
	// and run the request again.
	if (response.status === 401) {
		if (maxAttempts > 0 && attempt < maxAttempts) {
			sendGlobalEvent('refresh-token');
			return fetcher(input, options, attempt + 1);
		}
		sendGlobalEvent('force-end-session');
		log.warning(
			// Unlikely to be anything other than a string.
			// oxlint-disable-next-line typescript/no-base-to-string
			`401 Unauthorized at ${input.toString()}: refreshing token and retrying request failed after ${maxAttempts} attempts, logging out user`,
		);
	}

	throw new ResponseError({
		headers: response.headers,
		status: response.status,
		statusText: response.statusText,
		response: responseData,
		message: 'Unknown error',
	});
}

/** Do a request and get the parsed response. */
export async function fetchData<T = unknown>(
	input: string | URL | Request,
	options?: RequestOptions,
): Promise<T | undefined> {
	return fetcher(input, options);
}

/** Do a request and get a result type with the data or error. */
export async function fetchResult<T = unknown>(
	input: string | URL | Request,
	options?: RequestOptions,
): Promise<Result<T | undefined, FetchError>> {
	try {
		const response = await fetcher<T>(input, options);
		return success(response);
	} catch (error) {
		if (error instanceof Error) {
			return failure(error);
		}
	}
	return failure(new Error('Unknown error'));
}
